import {
  createContext,
  useCallback,
  useEffect,
  useState,
  useContext,
  useRef,
  type JSX,
} from 'react';
import { ProductsContext } from 'context-providers/products-provider';
import { LocationContext } from 'context-providers/location-provider';
import {
  persistPromoCode,
  getPersistedPromoCode,
  removePersistedPromoCode,
  getPromoCodeFromQueryParameters,
} from 'lib/promos';
import {
  activeMiniSale,
  isMiniSaleRouteExcluded,
} from 'lib/mini-sale/mini-sale';
import { PurchasePreviewInfo } from 'components/pages/checkout/entities';
import { setCookie, parseCookies, destroyCookie } from 'nookies';
import { Product } from 'entities/product';
import ProductData from 'lib/product-data';
import { useRouter } from 'next/router';
import {
  getFromLocalStorage,
  getFromLocalStorageAsString,
} from 'lib/utils/get-from-local-storage';
import {
  ANALYTICS_EVENTS,
  buildMetaUserData,
  propertiesForProduct,
  trackButtonEvent,
  trackEvent,
} from 'lib/analytics';
import { UserContext } from 'context-providers/user-provider';
import { useFeatureFlags } from './feature-flag-provider';
import { getCouponType } from 'lib/get-coupon-type';
import { useGetSubscriptions } from 'api/subscription';
import {
  apiGetShopifyCart,
  apiUpdateShopifyCart,
  apiCreateShopifyCart,
  apiAddShopifyDiscount,
  apiUpdateShopifyCartAttributes,
  apiUpdateCartBuyerIdentity,
} from 'lib/api';
import { getUrlParams } from 'lib/utils/get-url-params';
import { getMarketCountry } from 'lib/utils/get-market-country';
import { getMaxQuantity } from 'lib/constants/country-quantity-limits';
import { getUpsellSkusCoveredBySrp } from 'lib/srp';
import { getLocalizedPrice } from 'lib/utils/get-localized-price';
import { getUsdToMarketRatio } from 'lib/utils/usd-to-market-ratio';
import { OPEN_CART_QUERY_PARAM } from 'lib/utils/open-cart-query-param';
import {
  addReactivationToCart,
  AddReactivationToCartParams,
} from 'lib/utils/add-reactivation-to-cart';
import {
  OpenCartOptions,
  parseOpenCartUrl,
  runOpenCart,
} from 'lib/utils/cart/run-open-cart';
import { parseProductQuantities } from 'lib/utils/cart/update-cart-on-load';
import {
  getSegmentAnonymousIdSync,
  getSegmentAnonymousIdWithTimeout,
  getOrCreateFbc,
  getOrCreateFbp,
} from 'lib/segment';
import { getOptimizelyVisitorId } from 'lib/utils/get-optimizely-visitor-id';
import { getClientIp } from 'lib/client-ip';
import { getCleanedActiveGroups } from 'lib/cookie-consent';
import {
  lookupDiscountCode,
  resolveBestPromoCode,
  type PromoCodeDiscountInfo,
} from 'lib/utils/coupons/resolve-promo-code';
import { getPdpSkusForPath } from 'lib/utils/coupons/pdp-route-skus';
import {
  useOptimisticCart,
  type OptimisticHandle,
  type OptimisticAdd,
} from 'hooks/use-optimistic-cart';
import type {
  CartItemFieldsFragment,
  CartLineInput,
} from 'lib/shopify/generated/storefront';
import { PRODUCT_SKUS } from 'lib/constants/product-skus';
import { getBundleDiscountTotal } from 'lib/get-bundle-discount-total';

// hasUnappliedCodes is added to fragment by the server after data is fetched
// therefore we should extend the fragment type for use in the provider/elsewhere
export type ShopifyCart = CartItemFieldsFragment & {
  hasUnappliedCodes?: boolean;
};

type CartLineNode = CartItemFieldsFragment['lines']['nodes'][number];

const CART_ID_COOKIE_NAME = 'seed_shopify_cart_id';

type UserCartProps = {
  children: JSX.Element;
};

type CheckoutParameter = {
  key: string;
  value: string;
};

type UserCart = {};

export type CartUpdate = {
  updatedCart: UserCart;
};

export type {
  DiscountItems,
  PromoCodeDiscountInfo,
} from 'lib/utils/coupons/resolve-promo-code';

const defaultCart = {
  addToCart: async (_productCode: string, _quantity?: number) => ({
    updatedCart: {} as UserCart,
  }),
  removeFromCart: (_productCode: string, _removeAllOfItem?: boolean) => null,
  clearCart: () => null,
  getAutoCoupons: () => [] as string[],
  cartContains: (_productCode: string) => false as boolean,
  containsSrp: () => false as boolean,
  getSrpProduct: () => '' as string | null,
  getCartItems: () => [] as Array<string>,
  isEmpty: () => true as boolean,
  cart: {} as UserCart,
  count: (_productCode?: string) => 0 as number,
  cartId: () => '' as string,
  resetCartId: () => null,
  upsellItems: [] as Product[],
  replaceItem: async (_skuToReplace: string, _skuToAdd: string) => {},
  trackCheckoutButtonClicked: () => null,
  trackCartViewed: async () => {},
  getShopifyPricing: () => ({}) as PurchasePreviewInfo,
  fullCartInfo: {} as ShopifyCart,
  getCheckoutLink: (_extraParameters?: CheckoutParameter[]) => '' as string,
  getCartItemByShopifyIdOrSku: (_ID: string) =>
    undefined as CartLineNode | undefined,
  optUserOutOfBundleDiscount: async (_optedOut: boolean) => {},
  hasAutoCouponApplied: () => false as boolean,
  getCartDiscountAmount: () => 0 as number,
  getAutoDiscountCodeApplied: () => false as string | false,
};

export const defaultCartContext = {
  userCart: defaultCart,
  promoCode: '',
  setPromoCode: (_code: string) => null,
  promoCodeDiscountInfo: null as PromoCodeDiscountInfo,
  getShopifyPricing: () => null,
  isCartOpen: false,
  cartInitialized: false,
  canAddToCart: (_sku: string) => false as boolean,
  openCart: (_options?: OpenCartOptions) => {},
  openCartFromUrl: (_url: string) => {},
  closeCart: () => {},
  reactivateSubscriptionToCart: async (_params: AddReactivationToCartParams) =>
    false as boolean,
};

export const CartContext = createContext(defaultCartContext);

export const CartProvider: React.FC<UserCartProps> = ({
  children,
}: UserCartProps) => {
  const router = useRouter();

  // The active sale's own code, suppressed on the sale's excluded routes. It
  // competes against the URL and localStorage codes in resolveBestPromoCode
  // rather than being replaced by them.
  const getActiveSaleCode = (): string | null => {
    const miniSale = activeMiniSale();
    if (
      miniSale?.promoCode &&
      !isMiniSaleRouteExcluded(miniSale, router.asPath)
    ) {
      return miniSale.promoCode;
    }
    return null;
  };

  const { featureFlags } = useFeatureFlags();
  const isVsOutOfStock = featureFlags?.isVsOutOfStock;

  const { data: subscriptions } = useGetSubscriptions();
  const { countryCode, effectiveCountryCode } = useContext(LocationContext);
  // marketCountry resolves to 'US' while the international-pricing flag is
  // off; countryCode consumers keep the raw detected value.
  const isInternationalPricing = !!featureFlags?.internationalPricing;
  const marketCountry = getMarketCountry(
    effectiveCountryCode,
    isInternationalPricing,
  );
  const { products, getProduct, shopifyProducts, currencyCode } =
    useContext(ProductsContext);
  const { userState, signInState } = useContext(UserContext);

  const [upsellItems, setUpsellItems] = useState<Array<Product>>([]);

  // tracks items in the cart
  const [currentCartItems, setCurrentCartItems] = useState({});
  // tracks all Shopify cart info including discounts, price, etc.
  const [fullCartInfo, setFullCartInfo] = useState<ShopifyCart>(
    {} as ShopifyCart,
  );

  const [cartInitialized, setCartInitialized] = useState(false);

  // Refs for OneTrust consent handler to avoid memory leaks from stale closures
  const cartInitializedRef = useRef(cartInitialized);
  const fullCartInfoRef = useRef(fullCartInfo);

  const [promoCode, setPromoCodeInternal] = useState(
    getPersistedPromoCode() || '',
  );

  const [promoCodeDiscountInfo, setPromoCodeDiscountInfo] =
    useState<PromoCodeDiscountInfo>(null);

  // Codes whose 'Coupon Applied' event already fired (or was handled) this
  // session. The cart-mirror effect fires the event for codes not in this
  // set, so a code that only becomes applicable after the cart changes
  // (e.g. an SRP-only code applied before upgrading) is reported exactly
  // once, at the moment it actually applies.
  const reportedCouponCodesRef = useRef<Set<string>>(new Set());
  const markCouponReported = (code: string): void => {
    reportedCouponCodesRef.current.add(code.toUpperCase());
  };

  // Cart panel (slide-out) open state.
  const [isCartOpen, setIsCartOpen] = useState(false);
  const closeCart = useCallback(() => setIsCartOpen(false), []);

  const optimistic = useOptimisticCart({
    fullCartInfo,
    currentCartItems,
    setCurrentCartItems,
    setFullCartInfo,
    getProduct,
    shopifyProducts,
  });

  const productDataReady =
    Array.isArray(products) &&
    products.length > 0 &&
    !!shopifyProducts &&
    Object.keys(shopifyProducts).length > 0;

  const cartInitPathRef = useRef<string | null>(null);

  useEffect(() => {
    const needsProductData = !!(
      getUrlParams('cart') || getUrlParams('products')
    );
    if (needsProductData && !productDataReady) {
      return;
    }
    if (cartInitPathRef.current === router.pathname) {
      return;
    }
    cartInitPathRef.current = router.pathname;
    setCartFromStorageAndQueryParams();
  }, [router.pathname, productDataReady]);

  // Re-resolve on every route change, not just on load: the winning code
  // depends on the page — on a PDP, site-side codes that don't apply to the
  // viewed product are dropped and percentages are valued against that
  // product's price. productDataReady re-fires the multi-candidate comparison
  // that validateAndSetPromoCode defers until prices are loaded, and
  // currencyCode re-fires it when a country switch swaps the market currency.
  useEffect(() => {
    if (!cartInitialized) return;

    validateAndSetPromoCode();
  }, [cartInitialized, productDataReady, router.pathname, currencyCode]);

  // Once a cart exists, promoCode mirrors the codes actually discounting it.
  // The cart middleware strips non-applicable codes from every cart response,
  // so a code that isn't reducing the price never displays as applied. Before a
  // cart exists, promoCode holds the pending code to apply at cart creation.
  useEffect(() => {
    if (!fullCartInfo?.id) return;
    const appliedCode = fullCartInfo.discountCodes?.[0]?.code || '';
    // Shopify strips an ineligible code and an outcompeted one identically, so
    // tell them apart by whether anything is discounting the cart: if something
    // is, this code lost to it — keep it as user intent and it re-applies once
    // the cart changes.
    if (
      !appliedCode &&
      fullCartInfo.hasUnappliedCodes &&
      getBundleFunctionDiscountTotal(fullCartInfo) > 0
    ) {
      return;
    }
    setPromoCodeInternal((current: string) =>
      current.toUpperCase() === appliedCode.toUpperCase()
        ? current
        : appliedCode,
    );
    // Single firing point for auto-applied codes: a code attached to the cart
    // can start out inapplicable and only take effect after the cart changes
    // (e.g. an SRP-only code applied before upgrading). Paths that report or
    // restore a code themselves mark it first, so this never double-fires.
    if (
      appliedCode &&
      !reportedCouponCodesRef.current.has(appliedCode.toUpperCase())
    ) {
      markCouponReported(appliedCode);
      const { discountAmount, couponType } = getDiscountInfo(fullCartInfo);
      trackEvent('Coupon Applied', {
        coupon_id: appliedCode,
        coupon_name: appliedCode,
        discount: discountAmount,
        coupon_entry_method: 'auto',
        coupon_type: couponType,
      });
    }
  }, [fullCartInfo]);

  // Keep refs in sync for OneTrust consent handler
  useEffect(() => {
    cartInitializedRef.current = cartInitialized;
    fullCartInfoRef.current = fullCartInfo;
  }, [cartInitialized, fullCartInfo]);

  // Strip the given params from the URL without disturbing the rest. Built from
  // window.location.search rather than router.query: on a direct load of a
  // statically-optimized page (/cart uses getStaticProps), router.query is {}
  // until router.isReady, so router.replace({ pathname, query }) would wipe
  // params still in the address bar — notably promo_code, which activeMiniSale()
  // reads from window.location to resolve the sale override.
  const stripQueryParamsFromUrl = (keys: string[]): void => {
    const params = new URLSearchParams(window.location.search);
    keys.forEach((key) => params.delete(key));
    const search = params.toString();
    router.replace(
      `${window.location.pathname}${search ? `?${search}` : ''}`,
      undefined,
      { shallow: true },
    );
  };

  const setCartFromStorageAndQueryParams = async (): Promise<void> => {
    let data = getFromLocalStorage('seed_shopify_cart_items');
    const fullInfo = getFromLocalStorage(
      'seed_shopify_cart_full_info',
    ) as ShopifyCart | null;

    if (data) {
      setCurrentCartItems(data);
    }

    if (fullInfo) {
      // codes already on the restored cart were reported when first applied
      fullInfo.discountCodes?.forEach(
        (dc) => dc.code && markCouponReported(dc.code),
      );
      setFullCartInfo(fullInfo);
    }

    if (fullInfo?.discountCodes?.length === 0) {
      // if there are no codes then reset the bundle opt out
      const optOutAttribute =
        fullInfo.attributes?.find((attribute) => {
          return attribute.key === '_opted_out_of_bundle_discount';
        }) || false;
      if (optOutAttribute) {
        await optUserOutOfBundleDiscount(false);
      }
    }

    // HOTFIX: remove when gut reset is fixed
    let urlCartItems = getUrlParams('cart')?.replaceAll(
      PRODUCT_SKUS.GUT_RESET,
      '',
    );
    if (isVsOutOfStock) {
      urlCartItems = (urlCartItems || '')
        .replaceAll(PRODUCT_SKUS.VAGINAL_SYNBIOTIC, '')
        .replaceAll(PRODUCT_SKUS.VAGINAL_SYNBIOTIC_SUSTAIN, '');
    }

    // Products is a comma-separated list of product IDs from meta catalog
    // with colon and quantity, escaped according to RFC3986, e.g.
    // encoded: products=syn-wk%3A2%2Cdm-wk%3A1
    // decoded: products=syn-wk:2,dm-wk:1
    const urlProducts = getUrlParams('products');

    // A stored cart may have already gone through checkout — Shopify then
    // reports it as undefined and every update against it fails silently.
    // Validate it before any URL-param adds so a dead cart is cleared and
    // recreated instead of swallowing the add and leaving the cart empty.
    const storedCartID = cartId();
    if (storedCartID) {
      const validCart = await apiGetShopifyCart({ cartID: storedCartID });
      if (validCart.error && validCart.error.includes('cart undefined')) {
        // clearCart defers its cleanup; remove the cookie synchronously so
        // the adds below create a fresh cart instead of updating the dead one
        resetCartId();
        clearCart();
        data = null;
      }
    } else if (!urlProducts && !urlCartItems) {
      // no cart ID so just ensure we've cleared old cart items out of storage
      clearCart();
    }

    if (urlProducts) {
      const parsedProducts = parseProductQuantities(
        urlProducts,
        isVsOutOfStock,
      );
      const itemsToAdd = Object.entries(parsedProducts)
        .filter(([sku]) => !data?.[sku])
        .map(([sku, quantity]) => ({ productCode: sku, quantity }));
      if (itemsToAdd.length > 0) {
        await addItemsToCart(itemsToAdd);
      }

      stripQueryParamsFromUrl(['products', 'cart', OPEN_CART_QUERY_PARAM]);
    } else if (urlCartItems) {
      const itemsToAdd = urlCartItems
        .split(',')
        .filter((sku) => sku && !data?.[sku])
        .map((sku) => ({ productCode: sku }));
      if (itemsToAdd.length > 0) {
        await addItemsToCart(itemsToAdd);
      }

      stripQueryParamsFromUrl(['cart', OPEN_CART_QUERY_PARAM]);
    } else if (getUrlParams(OPEN_CART_QUERY_PARAM)) {
      // Bare /cart redirect (/products?open-cart=true) adds no items; strip the
      // marker so it can't linger in the URL and re-arm the panel on refresh.
      stripQueryParamsFromUrl([OPEN_CART_QUERY_PARAM]);
    }
    setCartInitialized(true);
  };

  const validateAndSetPromoCode = async (): Promise<void> => {
    const promoCodeFromUrl = getPromoCodeFromQueryParameters();
    const saleCode = getActiveSaleCode();
    const promoCodeFromLocalStorage = getPersistedPromoCode();

    // No promo codes at all — nothing to do
    if (!promoCodeFromUrl && !saleCode && !promoCodeFromLocalStorage) {
      return;
    }

    // On a PDP, percentages are valued against the viewed product's price and
    // site-side codes are checked for applicability to it; elsewhere the DS-01
    // price anchors the comparison. Candidates compare in the market currency:
    // the base price is localized and USD fixed amounts are converted by the
    // anchor product's observed FX ratio — Shopify converts fixed codes at
    // checkout the same way (verified on staging CAD carts).
    const pdpSkus = getPdpSkusForPath(router.asPath);
    const viewedProductIds = pdpSkus
      ?.map((sku) => getProduct(sku)?.shopifyData?.productID)
      .filter((id): id is string => !!id);
    const pdpProduct = pdpSkus ? getProduct(pdpSkus[0]) : null;
    const anchorProduct = getLocalizedPrice(pdpProduct)
      ? pdpProduct
      : getProduct(PRODUCT_SKUS.DAILY_SYNBIOTIC);
    const basePrice =
      parseFloat(getLocalizedPrice(anchorProduct) ?? '') || undefined;
    const usdToMarketRate = getUsdToMarketRatio(anchorProduct);

    // Comparing candidates values percentage codes at $0 until prices load,
    // which could pick the wrong winner — defer; the effect re-runs this when
    // product data is ready. A single candidate needs no price.
    const uniqueCandidateCount = new Set(
      [promoCodeFromUrl, saleCode, promoCodeFromLocalStorage]
        .filter((candidate): candidate is string => !!candidate)
        .map((candidate) => candidate.toUpperCase()),
    ).size;
    if (uniqueCandidateCount > 1 && !basePrice) {
      return;
    }

    const resolved = await resolveBestPromoCode(
      promoCodeFromUrl,
      [saleCode, promoCodeFromLocalStorage],
      { basePrice, viewedProductIds, usdToMarketRate },
    );

    if (!resolved) {
      // Shopify confirmed every candidate code is nonexistent — clear them so
      // an invalid code never displays as applied or rides along on a future
      // cart. Reset state to whatever is actually applied on the cart.
      removePersistedPromoCode();
      setPromoCodeDiscountInfo(null);
      setPromoCodeInternal(fullCartInfo?.discountCodes?.[0]?.code || '');
      return;
    }

    const { code, info } = resolved;

    // Persist even if the code can't discount the current cart — it's a real
    // code and may become applicable as the cart changes.
    persistPromoCode(code);
    setPromoCodeDiscountInfo(info);

    const currentCartId = cartId();
    if (!currentCartId) {
      // No cart yet — addToCart applies the code when the cart is created.
      setPromoCodeInternal(code);
      return;
    }

    const alreadyApplied = fullCartInfo?.discountCodes?.some(
      (dc) => dc.code?.toUpperCase() === code.toUpperCase(),
    );

    if (alreadyApplied) {
      // Already on the cart (e.g. applied during cart creation) — the
      // cart-mirror effect reported it.
      return;
    }

    // A code restored only from localStorage already fired its 'Coupon Applied'
    // event in the session that first applied it. A code arriving via the URL
    // or the active sale is fresh intent for this visit and should report once.
    const fromQueryParam =
      promoCodeFromUrl?.toUpperCase() === code.toUpperCase() ||
      saleCode?.toUpperCase() === code.toUpperCase();
    // Reserve the event before the await: while setPromoCode is in flight, the
    // cart-mirror effect or an overlapping run can report this same code.
    const shouldReport =
      fromQueryParam && !reportedCouponCodesRef.current.has(code.toUpperCase());
    if (shouldReport) {
      markCouponReported(code);
    }

    // setPromoCode only updates promoCode state through the cart response,
    // so a code Shopify rejects never shows as applied.
    const result = await setPromoCode(code);
    // Report a query-param code as user intent even when it never becomes the
    // applied code: the bundle discount can outcompete it — stripped from the
    // cart response (so the cart-mirror effect never fires), or returning false
    // here when no entitled line items remain. The bundle still wins the cart;
    // this records that the visit arrived intending to use the code.
    // reportedCouponCodesRef keeps it to exactly one event per session.
    if (shouldReport) {
      const { discountAmount, couponType } =
        getCodeDiscountInfo(info) ||
        (result
          ? {
              discountAmount: result.discountAmount,
              couponType: result.couponType,
            }
          : getDiscountInfo(fullCartInfo));
      trackEvent('Coupon Applied', {
        coupon_id: code,
        coupon_name: code,
        discount: discountAmount,
        coupon_entry_method: 'auto',
        coupon_type: couponType,
      });
    }
  };

  useEffect(() => {
    localStorage?.setItem(
      'seed_shopify_cart_items',
      JSON.stringify(currentCartItems),
    );
  }, [currentCartItems]);

  useEffect(() => {
    localStorage?.setItem(
      'seed_shopify_cart_full_info',
      JSON.stringify(fullCartInfo),
    );
  }, [fullCartInfo]);

  // When OneTrust consent changes and a cart exists, update the
  // __consent_categories cart attribute so the order always reflects
  // the latest consent state at time of checkout.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (typeof window.OneTrust?.OnConsentChanged !== 'function') return;

    const handleConsentChange = async () => {
      if (!cartInitializedRef.current) return;

      const currentCartId = cartId();
      if (!currentCartId) return; // no cart yet — next cart creation picks it up

      const consentGroups = getCleanedActiveGroups();
      if (!consentGroups) return;

      const preserved = (fullCartInfoRef.current.attributes || []).filter(
        (a) => a.key !== '__consent_categories',
      );

      const cartResponse = await apiUpdateShopifyCartAttributes({
        cartID: currentCartId,
        attributes: [
          ...preserved,
          { key: '__consent_categories', value: consentGroups },
        ],
      });

      if (cartResponse.error) {
        console.log('Failed to sync consent categories:', cartResponse);
        return;
      }

      const { fullShopifyCart, cart } = cartResponse?.data;
      setCurrentCartItems(cart);
      setFullCartInfo(fullShopifyCart);
    };

    window.OneTrust.OnConsentChanged(handleConsentChange);
    // OneTrust.OnConsentChanged doesn't return a cleanup handle, so no teardown.
    // Using refs ensures the handler always reads the latest values without
    // re-registering on every state change (which would cause a memory leak).
  }, []);

  // Email and market country sync in one effect so simultaneous changes send
  // a single update; the sequence ref drops stale responses.
  const buyerIdentitySyncSeq = useRef(0);
  useEffect(() => {
    const syncBuyerIdentity = async () => {
      const currentCartId = cartId();
      if (!currentCartId) return;

      const updates: { email?: string; countryCode?: string } = {};
      if (
        signInState === 'signedIn' &&
        userState?.email &&
        !fullCartInfo?.buyerIdentity?.email
      ) {
        updates.email = userState.email;
      }
      // Syncs when the flag is on, and also when a cart is still on a non-US
      // market after the flag turns off, so cart and UI currency match.
      const cartCountry = fullCartInfo?.buyerIdentity?.countryCode;
      const cartStuckOnNonUsMarket = !!cartCountry && cartCountry !== 'US';
      if (
        (isInternationalPricing || cartStuckOnNonUsMarket) &&
        fullCartInfo?.buyerIdentity &&
        cartCountry !== marketCountry
      ) {
        updates.countryCode = marketCountry;
      }
      // cartBuyerIdentityUpdate clears an omitted email,
      //  so a country-only update must carry the existing one.
      if (
        updates.countryCode &&
        !updates.email &&
        fullCartInfo?.buyerIdentity?.email
      ) {
        updates.email = fullCartInfo.buyerIdentity.email;
      }
      if (!Object.keys(updates).length) return;

      const seq = ++buyerIdentitySyncSeq.current;
      const cartResponse = await apiUpdateCartBuyerIdentity({
        cartID: currentCartId,
        ...updates,
      });
      if (seq !== buyerIdentitySyncSeq.current) return;

      if (cartResponse.error) {
        console.log(cartResponse);
        return;
      }

      // Drop the response if the cart was replaced (e.g. a reactivation)
      // while this was in flight — this effect re-runs for the new cart
      if (cartId() !== currentCartId) {
        return;
      }

      const { fullShopifyCart, cart } = cartResponse?.data;
      setCurrentCartItems(cart);
      setFullCartInfo(fullShopifyCart);
    };

    syncBuyerIdentity();
  }, [
    signInState,
    userState?.email,
    marketCountry,
    isInternationalPricing,
    fullCartInfo?.buyerIdentity?.email,
    fullCartInfo?.buyerIdentity?.countryCode,
  ]);

  const getCookieValue = (name: string): string | null => {
    const match = document.cookie.match(
      new RegExp(
        '(?:^|; )' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)',
      ),
    );
    return match ? decodeURIComponent(match[1]) : null;
  };

  const parseGaClientId = (gaCookie: string | null): string | null => {
    if (!gaCookie) return null;
    // _ga format: GA1.2.XXXXXXX.XXXXXXX — we want the last two segments
    const parts = gaCookie.split('.');
    if (parts.length < 4) return null;
    return parts.slice(-2).join('.');
  };

  // Write analytics identity attributes to the Shopify cart so they're
  // available on the checkout domain (including shop.app for Shop Pay).
  // Re-runs when the cart is created or when the user signs in.
  useEffect(() => {
    const currentCartId = cartId();
    if (!cartInitialized || !currentCartId) return;

    const sync = async () => {
      const mutationIdAtStart = optimistic.latestMutationId();
      // Use timeout-guarded version so analytics.user() can't hang forever
      // when Segment hasn't loaded (consent not granted, ad-blocker, etc.).
      // IP and anon-id are fetched independently so one failing doesn't
      // block the other.
      const [anonymousId, clientIp] = await Promise.allSettled([
        getSegmentAnonymousIdWithTimeout(),
        getClientIp(),
      ]).then((results) =>
        results.map((r) => (r.status === 'fulfilled' ? r.value : null)),
      );
      const fbp = getOrCreateFbp();
      const fbc = getOrCreateFbc();
      const gaClientId = parseGaClientId(getCookieValue('_ga'));
      const optimizelyVisitorId = getOptimizelyVisitorId();

      const analyticsAttrs: Array<{ key: string; value: string }> = [];
      if (anonymousId)
        analyticsAttrs.push({ key: '__segment_anon_id', value: anonymousId });
      if (gaClientId)
        analyticsAttrs.push({ key: '__ga_client_id', value: gaClientId });
      if (fbp) analyticsAttrs.push({ key: '__fbp', value: fbp });
      if (fbc) analyticsAttrs.push({ key: '__fbc', value: fbc });
      if (clientIp)
        analyticsAttrs.push({ key: '__client_ip', value: clientIp });
      if (optimizelyVisitorId)
        analyticsAttrs.push({
          key: '__optimizely_end_user_id',
          value: optimizelyVisitorId,
        });
      if (userState?.externalId)
        analyticsAttrs.push({
          key: '__seed_user_uuid',
          value: userState.externalId,
        });
      if (userState?.email)
        analyticsAttrs.push({
          key: '_originating_customer_email',
          value: userState.email,
        });
      if (userState?.externalId)
        analyticsAttrs.push({
          key: '_originating_customer_uuid',
          value: userState.externalId,
        });

      const utmCampaign = getFromLocalStorageAsString('utm_campaign_last');
      const utmSource = getFromLocalStorageAsString('utm_source_last');
      const utmMedium = getFromLocalStorageAsString('utm_medium_last');
      const utmTerm = getFromLocalStorageAsString('utm_term_last');
      const utmContent = getFromLocalStorageAsString('utm_content_last');
      const utmId = getFromLocalStorageAsString('utm_id_last');
      if (utmCampaign)
        analyticsAttrs.push({ key: '__utm_campaign', value: utmCampaign });
      if (utmSource)
        analyticsAttrs.push({ key: '__utm_source', value: utmSource });
      if (utmMedium)
        analyticsAttrs.push({ key: '__utm_medium', value: utmMedium });
      if (utmTerm) analyticsAttrs.push({ key: '__utm_term', value: utmTerm });
      if (utmContent)
        analyticsAttrs.push({ key: '__utm_content', value: utmContent });
      if (utmId) analyticsAttrs.push({ key: '__utm_id', value: utmId });

      // Include OneTrust consent categories so the order carries consent state.
      const consentGroups = getCleanedActiveGroups();
      if (consentGroups)
        analyticsAttrs.push({
          key: '__consent_categories',
          value: consentGroups,
        });

      if (analyticsAttrs.length === 0) return;

      const analyticsKeys = new Set(analyticsAttrs.map((a) => a.key));
      const preserved = (fullCartInfo.attributes || []).filter(
        (a) => !analyticsKeys.has(a.key),
      );

      // keepalive: true ensures the request completes even if the user
      // navigates to checkout before the response arrives.
      const cartResponse = await apiUpdateShopifyCartAttributes(
        {
          cartID: currentCartId,
          attributes: [...preserved, ...analyticsAttrs],
        },
        { keepalive: true },
      );

      if (cartResponse.error) {
        console.log('Failed to sync analytics attributes:', cartResponse);
        return;
      }

      // Ignore the response if the cart was swapped out (e.g. a reactivation)
      // while this request was running — this effect re-runs for the new cart
      if (cartId() !== currentCartId) {
        return;
      }

      const { fullShopifyCart } = cartResponse?.data;
      // After a cart change during the sync, this response has old lines, so only its attributes apply.
      if (optimistic.latestMutationId() === mutationIdAtStart) {
        optimistic.reconcile(null, fullShopifyCart);
      } else {
        setFullCartInfo((prev) => ({
          ...prev,
          attributes: fullShopifyCart.attributes,
        }));
      }
    };

    sync();
  }, [
    cartInitialized,
    fullCartInfo.id,
    userState?.externalId,
    userState?.email,
  ]);

  const optUserOutOfBundleDiscount = async (
    optedOut: boolean,
  ): Promise<void> => {
    const otherAttributes =
      fullCartInfo.attributes?.filter(
        (attribute) => attribute.key !== '_opted_out_of_bundle_discount',
      ) || [];

    const attributes = optedOut
      ? [
          ...otherAttributes,
          { key: '_opted_out_of_bundle_discount', value: 'true' },
        ]
      : otherAttributes;

    const cartResponse = await apiUpdateShopifyCartAttributes({
      cartID: cartId(),
      attributes,
    });
    if (cartResponse.error) {
      console.log(cartResponse);
      return;
    }

    const { fullShopifyCart, cart } = cartResponse?.data;
    setCurrentCartItems(cart);
    setFullCartInfo(fullShopifyCart);
  };

  const getBundleFunctionDiscountTotal = (cartInfo?: ShopifyCart): number =>
    getBundleDiscountTotal(cartInfo || fullCartInfo);

  const hasAutoCouponApplied = (): boolean => {
    if (!fullCartInfo) {
      return false;
    }

    // cart middleware only returns applicable coupons, so if there are any coupons added
    // then we have a manual coupon affecting the cart
    if (fullCartInfo?.discountCodes?.length > 0) {
      return false;
    }

    // Do NOT use getCartDiscountAmount() here — it also counts SRP compareAtPrice
    // savings and other automatic discounts, which are not bundle25.
    return getBundleFunctionDiscountTotal() > 0;
  };

  const getCartDiscountAmount = () => {
    const pricing = getShopifyPricing();
    if (
      pricing?.previewSubTotalPrice == null ||
      pricing?.previewTotalPrice == null
    ) {
      return 0;
    }
    const { previewSubTotalPrice, previewTotalPrice } = pricing;
    return Math.round(
      (100.0 * (previewSubTotalPrice - previewTotalPrice)) /
        previewSubTotalPrice,
    );
  };

  const getAutoDiscountCodeApplied = () => {
    // If a manual code is present, it is what's discounting the cart.
    if (fullCartInfo?.discountCodes?.length > 0) {
      return false;
    }

    if (getBundleFunctionDiscountTotal() > 0) {
      return 'BUNDLE25';
    }

    return false;
  };

  // Derives the discount amount and coupon type from a looked-up code's own
  // discount info (independent of the cart). A code outcompeted by the bundle
  // never shows on the cart, so getDiscountInfo would report the bundle's value
  // instead of the code's — this reports the code's own value for its event.
  const getCodeDiscountInfo = (
    info: PromoCodeDiscountInfo,
  ): { discountAmount: number; couponType: 'fixed' | 'percent' } | null => {
    if (!info?.value) return null;
    if (info.value.percentage) {
      return {
        discountAmount: Math.round(info.value.percentage * 100),
        couponType: 'percent',
      };
    }
    if (info.value.amount?.amount) {
      return {
        discountAmount: Math.round(parseFloat(info.value.amount.amount)),
        couponType: 'fixed',
      };
    }
    return null;
  };

  // Computes the discount amount and coupon type for a given cart.
  // Accepts an optional cart object; falls back to fullCartInfo state.
  const getDiscountInfo = (cartInfo?: ShopifyCart) => {
    const cart = cartInfo || fullCartInfo;
    const pricing = getShopifyPricing(cart);
    const couponType = getCouponType(cart);
    const discountAmount =
      pricing?.previewSubTotalPrice && pricing?.previewTotalPrice
        ? couponType === 'fixed'
          ? Math.round(pricing.previewSubTotalPrice - pricing.previewTotalPrice)
          : Math.round(
              (100.0 *
                (pricing.previewSubTotalPrice - pricing.previewTotalPrice)) /
                pricing.previewSubTotalPrice,
            )
        : 0;
    return { discountAmount, couponType };
  };

  const setPromoCode = async (code: string) => {
    const data = {
      cartID: cartId(),
    };

    data.discountCode = code;
    const cartResponse = await apiAddShopifyDiscount(data);

    if (cartResponse?.error || cartResponse?.data?.error) {
      console.log(cartResponse);
      return false;
    }

    // Only a code Shopify has no record of is refused outright. Its other
    // warnings — DISCOUNT_CODE_NOT_HONOURED above all — read identically whether
    // a code is ineligible or merely outcompeted, so the cart decides instead.
    if (cartResponse?.data.warnings) {
      const discountError = cartResponse.data.warnings.find(
        (warning) => warning.code === 'DISCOUNT_NOT_FOUND',
      );
      if (discountError) {
        return false;
      }
    }

    const { fullShopifyCart, cart } = cartResponse?.data;
    setCurrentCartItems(cart);
    setFullCartInfo(fullShopifyCart);

    // The middleware keeps only applicable codes, so a code missing here did
    // not apply. That is a failure only when nothing else is discounting the
    // cart; otherwise it was outcompeted and stays attached for later.
    const codeApplied =
      !code ||
      fullShopifyCart?.discountCodes?.some(
        (dc) => dc.code?.toUpperCase() === code.toUpperCase(),
      );
    if (!codeApplied && getBundleFunctionDiscountTotal(fullShopifyCart) === 0) {
      return false;
    }

    // Reaching here means the code either applied or lost to a bigger discount.
    // The cart-mirror effect keeps this value only in that second case, which is
    // what holds the promo row open to show the discount that did win.
    setPromoCodeInternal(code);

    if (code) {
      // callers report this application themselves (cart UI, validate flow)
      markCouponReported(code);
      persistPromoCode(code);
      // Codes typed into the cart skip resolveBestPromoCode; lookups are cached.
      lookupDiscountCode(code).then(({ info }) => {
        if (info) setPromoCodeDiscountInfo(info);
      });
    } else {
      removePersistedPromoCode();
      setPromoCodeDiscountInfo(null);
    }

    const { discountAmount, couponType } = getDiscountInfo(fullShopifyCart);

    return { success: true, discountAmount, couponType };
  };

  const getCartItems = (): Array<string> => {
    return !!currentCartItems ? Object.keys(currentCartItems) : [];
  };

  const replaceItem = async (skuToReplace, skuToAdd) => {
    const newProduct = getProduct(skuToAdd);
    const cartID = cartId();
    const lineID = getCartLineID(skuToReplace);

    const quantity = Math.min(
      count(skuToReplace),
      getMaxQuantity(skuToAdd, effectiveCountryCode, newProduct.maxQuantity),
    );

    const cartUpdate = {
      cartID,
      lines: [
        {
          merchandiseId: newProduct.shopifyData.id,
          sellingPlanId:
            newProduct.shopifyData.sellingPlanAllocations.edges[0]?.node
              ?.sellingPlan?.id,
          quantity: quantity,
          id: lineID,
        },
      ],
    };

    // sync gift line to new parent quantity (orphan cleanup handles removal)
    const giftWithProduct = resolveGiftData(skuToAdd);
    if (giftWithProduct?.value) {
      const giftItem = getCartItemByShopifyIdOrSku(giftWithProduct.value);
      cartUpdate.lines.push({
        merchandiseId: giftWithProduct.value,
        quantity,
        id: giftItem?.id || undefined,
        attributes: [{ key: GIFT_ATTR_KEY, value: 'true' }],
      });
    }

    const cartResponse = await apiUpdateShopifyCart(cartUpdate);

    if (cartResponse.error) {
      console.log(cartResponse);
      return;
    }

    await setCartFromResponse(cartResponse.data);
  };

  const getAutoCoupons = (): string[] => {
    const autoCoupon = getCartItems().map((itemCode) => {
      const product = products.find((p) => p.code === itemCode);
      if (product && product.domesticAttributes.autoCouponCode) {
        return product.domesticAttributes.autoCouponCode;
      }
      return;
    });

    return autoCoupon[0] ? [autoCoupon[0]] : [];
  };

  // Always use the root path so there's one cart-id cookie for the whole
  // site. Without it the browser keeps a separate copy per page path, and a
  // leftover copy can point at an old cart.
  const resetCartId = (): void => {
    destroyCookie(null, CART_ID_COOKIE_NAME, { path: '/' });
  };

  const clearCart = (): void => {
    // make sure this runs after any other queued setup
    setTimeout(() => {
      resetCartId();
      localStorage.removeItem('seed_shopify_cart_items');
      localStorage.removeItem('seed_shopify_cart_full_info');
      setCurrentCartItems({});
      setFullCartInfo({} as ShopifyCart);
    }, 0);
  };

  const GIFT_ATTR_KEY = '_isGiftWithPurchase';

  const count = (productCode: string): number => {
    if (productCode) {
      return Number(currentCartItems[productCode] || 0);
    }
    let newCount = 0;
    getCartItems().forEach((code) => {
      const lineItem = fullCartInfo.lines?.nodes?.find(
        (item) => item.merchandise.sku === code,
      );
      const isGift = lineItem?.attributes?.find(
        (attr) => attr.key === GIFT_ATTR_KEY,
      );
      if (!isGift) {
        newCount += parseInt(currentCartItems[code], 10);
      }
    });
    return Number(newCount);
  };

  // Resolve giftWithProduct metafield for a SKU, falling back to the
  // base product for SRP variants (e.g. syn-wk-3mo → syn-wk).
  const resolveGiftData = (sku: string) => {
    const product = getProduct(sku);
    const baseCode = sku.replace(/-\d+mo$/, '');
    return (
      product?.shopifyData?.giftWithProduct ??
      (baseCode !== sku
        ? getProduct(baseCode)?.shopifyData?.giftWithProduct
        : null)
    );
  };

  const getCartItemByShopifyIdOrSku = (
    ID: string,
  ): CartLineNode | undefined => {
    return fullCartInfo.lines?.nodes?.find((item) => {
      return item.merchandise.id === ID || item.merchandise.sku === ID;
    });
  };

  // Remove gift line items whose parent product is no longer in the cart.
  // Uses giftWithProduct metafield as source of truth.
  const removeOrphanGifts = async (fullShopifyCart: ShopifyCart) => {
    const nodes = fullShopifyCart?.lines?.nodes;
    if (!nodes?.length) return null;

    const giftLines = nodes.filter((item) =>
      item.attributes?.some((attr) => attr.key === GIFT_ATTR_KEY),
    );
    if (!giftLines.length) return null;

    const giftLineIds = new Set(giftLines.map((g) => g.id));
    const expectedGiftIds = new Set<string>();
    for (const item of nodes) {
      if (giftLineIds.has(item.id)) continue;
      const sku = item.merchandise?.sku;
      if (!sku) continue;
      const giftData = resolveGiftData(sku);
      if (giftData?.value) {
        expectedGiftIds.add(giftData.value);
      }
    }

    const orphans = giftLines.filter(
      (gift) => !expectedGiftIds.has(gift.merchandise.id),
    );
    if (!orphans.length) return null;

    const cartUpdate = {
      cartID: fullShopifyCart.id,
      lines: orphans.map((gift) => ({
        id: gift.id,
        merchandiseId: gift.merchandise.id,
        quantity: 0,
      })),
    };

    const response = await apiUpdateShopifyCart(cartUpdate);
    if (response.error) {
      console.error('Failed to remove orphan gifts:', response);
      return null;
    }
    return response.data;
  };

  // Run orphan gift cleanup then set cart state.
  const setCartFromResponse = async (
    responseData: {
      fullShopifyCart: ShopifyCart;
      cart: UserCart;
    },
    opt?: OptimisticHandle | null,
  ) => {
    const cleaned = await removeOrphanGifts(responseData.fullShopifyCart);
    const { fullShopifyCart } = cleaned ?? responseData;
    optimistic.reconcile(opt ?? null, fullShopifyCart);
  };

  const canAddToCart = (sku: string): boolean => {
    if (sku === PRODUCT_SKUS.GUT_RESET) return false;
    const product = getProduct(sku);
    if (!product?.shopifyData?.id) return false;
    // A zero cap means unavailable, not a request to remove an existing line.
    return getMaxQuantity(sku, effectiveCountryCode, product.maxQuantity) >= 1;
  };

  // Prepare every item before updating state so bundles appear together.
  const addItemsToCart = async (
    itemsToAdd: Array<{ productCode: string; quantity?: number }>,
  ): Promise<CartUpdate> => {
    const quantities = new Map<string, number>();
    for (const { productCode, quantity = 1 } of itemsToAdd) {
      const sku = productCode.trim().toLowerCase();
      quantities.set(sku, (quantities.get(sku) || 0) + quantity);
    }

    const lines: Array<
      Partial<CartLineInput> & {
        merchandiseId: string;
        id?: string;
        quantity: number;
      }
    > = [];
    const giftLines = new Map<string, (typeof lines)[number]>();
    const productsAdded: Product[] = [];
    const optimisticAdds: OptimisticAdd[] = [];
    for (const [sku, quantity] of quantities) {
      const product = getProduct(sku);
      const variant = product?.shopifyData;
      if (!variant?.id || !canAddToCart(sku)) continue;
      const maxQuantity = getMaxQuantity(
        sku,
        effectiveCountryCode,
        product.maxQuantity,
      );

      const newQuantity = Math.min(count(sku) + quantity, maxQuantity);
      const lineId = getCartLineID(sku) || undefined;
      const id = optimistic.isOptimisticLineId(lineId) ? undefined : lineId;
      lines.push({
        merchandiseId: variant.id,
        sellingPlanId:
          variant.sellingPlanAllocations?.edges[0]?.node?.sellingPlan?.id,
        quantity: newQuantity,
        id,
      });

      const giftGID = resolveGiftData(sku)?.value;
      if (giftGID) {
        // Shared gifts use the last product's quantity, as sequential adds do.
        giftLines.set(giftGID, {
          merchandiseId: giftGID,
          quantity: newQuantity,
          id: getCartItemByShopifyIdOrSku(giftGID)?.id || undefined,
          attributes: [{ key: GIFT_ATTR_KEY, value: 'true' }],
        });
      }
      productsAdded.push(product);
      if (!id) optimisticAdds.push({ sku, quantity: newQuantity, giftGID });
    }
    if (!lines.length) return { updatedCart: currentCartItems };

    const cartID = cartId();
    // Shopify displays the last added line first; gifts go below paid products.
    const cartUpdate: Parameters<typeof apiCreateShopifyCart>[0] = {
      lines: [...giftLines.values(), ...lines],
    };
    let cartResponse;
    const optimisticHandle = optimistic.tryOptimisticAddMany(
      optimisticAdds,
      cartUpdate.lines,
    );
    if (cartID) {
      cartResponse = await apiUpdateShopifyCart({ ...cartUpdate, cartID });
    } else {
      // only need to add on cart create
      // Clone existing attributes to avoid mutating React state in-place,
      // then upsert by key so we never create duplicate attribute entries.
      const createAttrs: Array<{ key: string; value: string }> = (
        fullCartInfo?.attributes || []
      ).filter((a): a is { key: string; value: string } => a.value != null);
      const upsertAttr = (key: string, value: string) => {
        const idx = createAttrs.findIndex((a) => a.key === key);
        if (idx >= 0) {
          createAttrs[idx] = { key, value };
        } else {
          createAttrs.push({ key, value });
        }
      };

      if (userState?.experiments) {
        upsertAttr('_experiment', JSON.stringify(userState.experiments));
      }

      // Eagerly attach analytics identity attrs at cart-creation time so
      // they're present from the very first Shopify request. Uses synchronous
      // cookie reads to avoid delaying cart creation. The background sync
      // useEffect will enrich with async data (IP) afterwards.
      const syncAnonId = getSegmentAnonymousIdSync();
      const createFbp = getOrCreateFbp();
      const createFbc = getOrCreateFbc();
      const createGaClientId = parseGaClientId(getCookieValue('_ga'));
      const createOptimizelyVisitorId = getOptimizelyVisitorId();
      if (syncAnonId) upsertAttr('__segment_anon_id', syncAnonId);
      if (createGaClientId) upsertAttr('__ga_client_id', createGaClientId);
      if (createFbp) upsertAttr('__fbp', createFbp);
      if (createFbc) upsertAttr('__fbc', createFbc);
      if (createOptimizelyVisitorId)
        upsertAttr('__optimizely_end_user_id', createOptimizelyVisitorId);
      if (userState?.externalId)
        upsertAttr('__seed_user_uuid', userState.externalId);

      const createUtmCampaign =
        getFromLocalStorageAsString('utm_campaign_last');
      const createUtmSource = getFromLocalStorageAsString('utm_source_last');
      const createUtmMedium = getFromLocalStorageAsString('utm_medium_last');
      const createUtmTerm = getFromLocalStorageAsString('utm_term_last');
      const createUtmContent = getFromLocalStorageAsString('utm_content_last');
      const createUtmId = getFromLocalStorageAsString('utm_id_last');
      if (createUtmCampaign) upsertAttr('__utm_campaign', createUtmCampaign);
      if (createUtmSource) upsertAttr('__utm_source', createUtmSource);
      if (createUtmMedium) upsertAttr('__utm_medium', createUtmMedium);
      if (createUtmTerm) upsertAttr('__utm_term', createUtmTerm);
      if (createUtmContent) upsertAttr('__utm_content', createUtmContent);
      if (createUtmId) upsertAttr('__utm_id', createUtmId);

      // Attach OneTrust consent categories so they're available on the order.
      const createConsentGroups = getCleanedActiveGroups();
      if (createConsentGroups)
        upsertAttr('__consent_categories', createConsentGroups);

      cartUpdate.attributes = createAttrs;

      // Pre-resolution fallback only — validateAndSetPromoCode replaces the
      // cart's code with the resolved winner once its lookups finish.
      const resolvedPromoCode =
        promoCode ||
        getPromoCodeFromQueryParameters() ||
        getActiveSaleCode() ||
        getPersistedPromoCode();
      if (resolvedPromoCode) {
        cartUpdate.discountCodes = [resolvedPromoCode];
      }
      if (userState?.email) {
        cartUpdate.buyerEmail = userState.email;
      }
      cartUpdate.countryCode = marketCountry;
      cartResponse = await apiCreateShopifyCart(cartUpdate);
    }

    if (cartResponse.error) {
      optimistic.rollbackOptimisticIfLatest(optimisticHandle);
      console.log(cartResponse);
      return;
    }

    const { fullShopifyCart, cart } = cartResponse.data;
    // Track cart and full cart details seperate for compatibility with rest of site.
    // Skip the setState when a newer mutation is in flight — its response will reconcile.
    optimistic.reconcile(optimisticHandle, fullShopifyCart);

    // ensure cart ID is set
    setCookie(null, CART_ID_COOKIE_NAME, fullShopifyCart.id, { path: '/' });

    const userData = await buildMetaUserData(userState);
    for (const product of productsAdded) {
      trackEvent(ANALYTICS_EVENTS.PRODUCT_ADDED, {
        cart_id: cartId(),
        ...propertiesForProduct(product),
        ...(userData ? { userData } : {}),
      });
    }

    // If this new cart was created with a discount code that Shopify actually
    // applied (creation silently accepts invalid codes, and the middleware
    // strips non-applicable ones from the response), persist it so the
    // validate flow treats it as established user intent. The cart-mirror
    // effect handles both the promoCode state and the Coupon Applied event.
    if (!cartID && cartUpdate.discountCodes?.length) {
      const discountCode = cartUpdate.discountCodes[0];
      const wasApplied = fullShopifyCart?.discountCodes?.some(
        (dc) => dc.code?.toUpperCase() === discountCode.toUpperCase(),
      );
      if (wasApplied) {
        persistPromoCode(discountCode);
      }
    }

    return {
      updatedCart: cart,
    };
  };

  const addToCart = (
    selectedProductCode: string,
    quantity = 1,
  ): Promise<CartUpdate> =>
    addItemsToCart([{ productCode: selectedProductCode, quantity }]);

  const getCartLineItem = (sku: string): CartLineNode | undefined => {
    return fullCartInfo?.lines?.nodes?.find((lineItemToCheck) => {
      return lineItemToCheck.merchandise.sku === sku;
    });
  };

  const getCartLineID = (sku: string): string | false => {
    const lineItem = getCartLineItem(sku);
    return lineItem?.id || false;
  };

  const cartContains = (productCode: string): boolean =>
    getCartItems().includes(productCode);

  const removeFromCart = async (
    productCode: string,
    removeAllOfItem = false,
  ): void => {
    const cartID = cartId();
    const newQuantity = removeAllOfItem ? 0 : currentCartItems[productCode] - 1;
    const lineItem = getCartLineItem(productCode);

    if (!cartContains(productCode) || !cartID || !lineItem) {
      return;
    }

    // Get merchandise data directly from cart line item instead of getProduct
    const merchandiseId = lineItem.merchandise.id;

    let cartUpdate = {
      cartID,
      lines: [
        {
          id: lineItem.id,
          merchandiseId,
          quantity: newQuantity,
        },
      ],
    };

    const product = getProduct(productCode);
    const giftProductData = resolveGiftData(productCode);

    if (giftProductData?.value) {
      const giftItem = getCartItemByShopifyIdOrSku(giftProductData.value);
      // A gift shared with another product stays when this one is removed.
      const sharedWithOtherLine =
        newQuantity <= 0 &&
        fullCartInfo.lines?.nodes?.some(
          (line) =>
            line.id !== lineItem.id &&
            line.id !== giftItem?.id &&
            !!line.merchandise?.sku &&
            resolveGiftData(line.merchandise.sku)?.value ===
              giftProductData.value,
        );
      if (giftItem?.id && !sharedWithOtherLine) {
        cartUpdate.lines.push({
          merchandiseId: giftProductData.value,
          quantity: newQuantity,
          id: giftItem.id,
        });
      }
    }

    const optimisticHandle = optimistic.tryOptimisticUpdate(
      cartID,
      cartUpdate.lines,
    );
    const cartResponse = await apiUpdateShopifyCart(cartUpdate);

    if (cartResponse.error) {
      optimistic.rollbackOptimisticIfLatest(optimisticHandle);
      console.log(cartResponse);
      return;
    }

    // Track quantity decrease
    trackEvent(ANALYTICS_EVENTS.ITEM_REMOVED_FROM_CART, {
      cart_id: cartId(),
      ...propertiesForProduct(product),
    });

    await setCartFromResponse(cartResponse.data, optimisticHandle);
  };

  const containsSrp = (): boolean =>
    getCartItems().some(
      (productCode) =>
        products.find((product) => product.code === productCode)?.isSrp,
    );

  const getSrpProduct = (): string | null =>
    getCartItems().filter(
      (productCode) =>
        products.find((product) => product.code === productCode)?.isSrp,
    )[0];

  const isEmpty = (): boolean => getCartItems().length === 0;

  const cartId = (): string => {
    const cookies = parseCookies();
    let cookieId = cookies[CART_ID_COOKIE_NAME];
    return cookieId;
  };

  // This allows devs to still use userCart[PRODUCT_SKU] syntax instead of having to do userCart.cart[PRODUCT_SKU]
  // Note: For internal use only
  const cartQuantities = (): object => {
    // This function is being run on every page load no matter what, even when it's not being used.
    // The absence of the products array indicates that this function is not needed, so we can skip.
    // TODO-- make this function not be run on every load.
    if (!products) {
      return {};
    }

    const possibleProducts = {};

    products.forEach((product) => {
      if (
        product &&
        currentCartItems &&
        currentCartItems.hasOwnProperty(product.code)
      ) {
        possibleProducts[product.code] = currentCartItems[product.code] ?? 0;
      }
    });

    return possibleProducts;
  };

  const upsellableItems: string[] =
    countryCode === 'US'
      ? [
          PRODUCT_SKUS.DAILY_SYNBIOTIC,
          PRODUCT_SKUS.DAILY_MULTIVITAMIN,
          PRODUCT_SKUS.ENERGY_FOCUS,
          PRODUCT_SKUS.SLEEP_RESTORE,
          PRODUCT_SKUS.PDS08_DAILY_KIDS_PROBIOTIC,
          PRODUCT_SKUS.VAGINAL_SYNBIOTIC,
        ]
      : [
          PRODUCT_SKUS.DAILY_SYNBIOTIC,
          PRODUCT_SKUS.DAILY_MULTIVITAMIN,
          PRODUCT_SKUS.ENERGY_FOCUS,
          PRODUCT_SKUS.SLEEP_RESTORE,
          PRODUCT_SKUS.PDS08_DAILY_KIDS_PROBIOTIC,
        ];

  const getUpsellItems = () => {
    let potentialUpSellItems = products.filter((product) =>
      upsellableItems.includes(product.code),
    );
    if (subscriptions) {
      //remove products the user already has an active subscription to
      potentialUpSellItems = potentialUpSellItems.filter((product) => {
        const refillSku = product.code.split('-')[0] + '-rf';
        return !subscriptions.some(
          (sub) =>
            (refillSku === sub.planCode || product.code === sub.planCode) &&
            sub.subscriptionState === 'AC',
        );
      });
    }

    if (isVsOutOfStock) {
      potentialUpSellItems = potentialUpSellItems.filter(
        (product) => product.code !== PRODUCT_SKUS.VAGINAL_SYNBIOTIC,
      );
    }

    const srpCoveredSkus = getUpsellSkusCoveredBySrp(getCartItems());
    potentialUpSellItems = potentialUpSellItems.filter(
      (product) => !srpCoveredSkus.includes(product.code),
    );

    potentialUpSellItems.sort(
      (a, b) =>
        ProductData[a.code].upsell.displayOrder -
        ProductData[b.code].upsell.displayOrder,
    );

    // DS-01 already in the cart as SRP or one-time shouldn't be re-pitched as the monthly plan.
    const standardUpsellProducts =
      containsSrp() || cartContains(PRODUCT_SKUS.DAILY_SYNBIOTIC_ONE_TIME)
        ? potentialUpSellItems.filter(
            (product) =>
              !cartContains(product.code) &&
              product.code !== PRODUCT_SKUS.DAILY_SYNBIOTIC,
          )
        : potentialUpSellItems.filter((product) => !cartContains(product.code));

    const upsellProducts = !cartContains(PRODUCT_SKUS.GUT_RESET)
      ? standardUpsellProducts
      : standardUpsellProducts.filter(
          (product) =>
            product.code !== PRODUCT_SKUS.DAILY_SYNBIOTIC &&
            product.code !== PRODUCT_SKUS.PDS08_DAILY_KIDS_PROBIOTIC &&
            product.code !== PRODUCT_SKUS.VAGINAL_SYNBIOTIC,
        );

    // Resolve through getProduct so each upsell carries market-localized
    // shopifyData; the raw plans list only has USD prices.
    setUpsellItems(upsellProducts.map((product) => getProduct(product.code)));
  };

  useEffect(() => {
    if (products?.length) {
      getUpsellItems();
    }
  }, [products, currentCartItems, subscriptions, shopifyProducts]);

  const trackCheckoutButtonClicked = () => {
    trackButtonEvent('Checkout', 'cart', {
      cart_id: cartId(),
    });
  };

  const trackCartViewed = async () => {
    const userData = await buildMetaUserData(userState);
    trackEvent(ANALYTICS_EVENTS.CART_VIEWED, {
      cart_id: cartId(),
      ...(userData ? { userData } : {}),
    });
  };

  const getCheckoutLink = (extraParameters: CheckoutParameter[] = []) => {
    if (!fullCartInfo?.checkoutUrl) {
      return '';
    }

    const checkoutURL = new URL(fullCartInfo.checkoutUrl);

    extraParameters.forEach((parameter) => {
      if (parameter.key && parameter.value) {
        checkoutURL.searchParams.append(parameter.key, parameter.value);
      }
    });

    if (signInState === 'signedIn') {
      checkoutURL.searchParams.append('logged_in', 'true');
    }

    return checkoutURL.toString();
  };

  // Full cart info updates asynchronously, and the new variable values are not always available to us when this function is called.
  // Passing a cart variable here lets us avoid state issues.
  const getShopifyPricing = (cart?: ShopifyCart) => {
    const fullCartInfoObj = cart || fullCartInfo;
    const totalPrice = parseFloat(
      fullCartInfoObj?.cost?.totalAmount?.amount || '0',
    );

    // Discounts can appear in three different sections of fullCartInfo.
    // We sum all three to accurately show the total discount amount.

    // Cart-level allocations: bundle discount in the order-level model.
    // Will be empty in the product-level model (transition period — support both).
    const cartLevelDiscount = (
      fullCartInfoObj?.discountAllocations || []
    ).reduce(
      (acc, allocation) =>
        acc + parseFloat(allocation.discountedAmount?.amount || '0'),
      0,
    );

    // Line-level allocations: manual promo codes, and bundle discount in the
    // product-level model. Both allocation models sum correctly here.
    const lineLevelDiscount = (fullCartInfoObj?.lines?.nodes || []).reduce(
      (acc, line) =>
        acc +
        (line.discountAllocations || []).reduce(
          (lineAcc, allocation) =>
            lineAcc + parseFloat(allocation.discountedAmount?.amount || '0'),
          0,
        ),
      0,
    );

    // discount that should apply when "compare at price" is in shopify
    // in theory this will be used for sitewide promos? not sure
    const salePriceDiscount = (fullCartInfoObj?.lines?.nodes || []).reduce(
      (acc, line) => {
        const compareAtPrice = parseFloat(
          line.merchandise?.compareAtPrice?.amount || '0',
        );
        const lineSubtotal = parseFloat(
          line.estimatedCost?.subtotalAmount?.amount || '0',
        );
        const compareAtTotal = compareAtPrice * line.quantity;
        return acc + Math.max(0, compareAtTotal - lineSubtotal);
      },
      0,
    );

    const previewDiscountPrice =
      cartLevelDiscount + lineLevelDiscount + salePriceDiscount;
    const subtotalPrice = totalPrice + previewDiscountPrice;

    return {
      previewDiscountPrice,
      previewSubTotalPrice: subtotalPrice,
      previewTotalPrice: totalPrice,
      invoiceLineItems: fullCartInfoObj?.lines?.nodes || [],
    };
  };

  /**
   * Opens the cart panel; with options, first adds SKUs and applies/persists
   * a promo code the same way legacy /cart?cart=&promo_code= links did.
   * Bare openCart() just opens the panel.
   */
  const openCart = (options: OpenCartOptions = {}) => {
    runOpenCart(options, {
      cart: currentCartItems,
      addItemsToCart,
      setPromoCode,
      openPanel: () => setIsCartOpen(true),
    });
  };

  // For call sites whose product data is stored as a legacy /cart URL string
  // (e.g. product-data buyUrl entries like '/cart?cart=syn-wk,dm-wk').
  const openCartFromUrl = (url: string) => {
    openCart(parseOpenCartUrl(url));
  };

  /**
   * Replaces the cart with a fresh reactivation cart and opens the flyout in
   * place. The email-link flow builds the same cart but needs a full reload to
   * sync React state; this syncs it directly so in-app CTAs skip the redirect.
   */
  const reactivateSubscriptionToCart = async (
    params: AddReactivationToCartParams,
  ): Promise<boolean> => {
    const result = await addReactivationToCart(params);
    if (!result.success || !result.data) {
      return false;
    }
    setCurrentCartItems(result.data.cart);
    setFullCartInfo(result.data.fullShopifyCart);
    setIsCartOpen(true);

    // Give the new cart the member's saved promo code, the same way a page
    // load would. Not awaited so the flyout opens right away.
    const persistedPromo = getPersistedPromoCode();
    if (persistedPromo) {
      setPromoCode(persistedPromo);
    }
    return true;
  };

  // Open the cart panel on initial page load. ?cart= and ?open-cart= are
  // distinct on purpose and should NOT be merged: ?cart=<skus> is a payload
  // that ADDS those SKUs (legacy buy-link); ?open-cart=true is a pure REVEAL
  // signal (appended by the /cart redirect) that adds nothing. Either way the
  // panel opens only once the cart has items — there is no empty state, so an
  // empty cart never pops the panel. One-shot; abandoned on the first
  // client-side navigation so the panel can't surprise-open mid-session.
  const initialUrlParamsRef = useRef(
    typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search)
      : null,
  );
  const openOnLoadHandledRef = useRef(false);

  useEffect(() => {
    // Abandon the open-on-load intent only on a real navigation to a different
    // page. The cart provider strips ?cart=/open-cart via a shallow
    // router.replace on the SAME path right after adding items — that param
    // strip must not count as navigating away, or the panel never opens.
    const abandon = (url: string) => {
      try {
        if (
          new URL(url, window.location.origin).pathname ===
          window.location.pathname
        ) {
          return;
        }
      } catch {
        return;
      }
      openOnLoadHandledRef.current = true;
    };
    router.events.on('routeChangeStart', abandon);
    return () => {
      router.events.off('routeChangeStart', abandon);
    };
  }, [router.events]);

  useEffect(() => {
    if (openOnLoadHandledRef.current || !initialUrlParamsRef.current) {
      return;
    }

    const hasCartParam = !!initialUrlParamsRef.current.get('cart');
    const hasOpenCartParam =
      initialUrlParamsRef.current.get(OPEN_CART_QUERY_PARAM) === 'true';
    const cartHasItems = Object.keys(currentCartItems).length > 0;

    // Open once the cart has items. For ?cart=SKU those items are being added
    // this load — this effect re-runs as currentCartItems fills (optimistic
    // adds land as soon as product data is ready) and opens then. Opening
    // while still empty would trip the flyout's close-when-empty rule.
    if ((hasCartParam || hasOpenCartParam) && cartHasItems) {
      openOnLoadHandledRef.current = true;
      setIsCartOpen(true);
      return;
    }

    // Marker landed on an empty cart post-init: abandon the intent so a later
    // add doesn't re-fire this effect and pop the panel
    if (hasOpenCartParam && cartInitialized && !cartHasItems) {
      openOnLoadHandledRef.current = true;
    }
  }, [currentCartItems, cartInitialized]);

  const cart = {
    userCart: {
      getAutoCoupons,
      cart: currentCartItems,
      addToCart,
      replaceItem,
      removeFromCart,
      cartContains,
      clearCart,
      getCartItems,
      containsSrp,
      getSrpProduct,
      upsellItems,
      count,
      isEmpty,
      cartId,
      resetCartId,
      // Add analytics methods
      trackCheckoutButtonClicked,
      trackCartViewed,
      ...cartQuantities(),
      getCheckoutLink,
      getShopifyPricing,
      fullCartInfo,
      getCartItemByShopifyIdOrSku,
      optUserOutOfBundleDiscount,
      hasAutoCouponApplied,
      getCartDiscountAmount,
      getAutoDiscountCodeApplied,
    },
    promoCode,
    setPromoCode,
    promoCodeDiscountInfo,
    isCartOpen,
    cartInitialized,
    canAddToCart,
    openCart,
    openCartFromUrl,
    closeCart,
    reactivateSubscriptionToCart,
  };

  // @ts-ignore
  return <CartContext.Provider value={cart}>{children}</CartContext.Provider>;
};
